import { DealListSort, DealSalesScope } from '@/lib/graphql/generated/graphql';
import {
  DEAL_TABS,
  DEFAULT_DEALS_LIST_STATE,
  type DealsListState,
} from '@/states/deals-list.state';

export function parseDealsListParams(params: URLSearchParams): DealsListState {
  const defaults = DEFAULT_DEALS_LIST_STATE;
  const tab =
    DEAL_TABS.find((tab) => tab === params.get('tab')) ?? defaults.tab;
  const sorts = { ...defaults.sorts };
  for (const tab of DEAL_TABS) {
    const sort = params.get(`sort.${tab}`);
    if (sort === DealListSort.Asc || sort === DealListSort.Desc)
      sorts[tab] = sort;
  }
  const managerIds = [
    ...new Set(params.getAll('managerId').filter((id) => id.trim())),
  ];
  const scope = params.get('salesScope');
  const salesScope =
    scope === DealSalesScope.All
      ? DealSalesScope.All
      : scope === DealSalesScope.Selected && managerIds.length > 0
        ? DealSalesScope.Selected
        : DealSalesScope.Me;
  const page = Number(params.get('page'));
  const searchText = params.get('q') ?? '';
  return {
    tab,
    sorts,
    page: Number.isSafeInteger(page) && page > 0 ? page : 1,
    sales: {
      salesScope,
      managerIds: salesScope === DealSalesScope.Selected ? managerIds : [],
    },
    searchText,
    search: searchText.trim(),
  };
}

export function serializeDealsListParams(
  state: DealsListState,
  params = new URLSearchParams()
) {
  const result = new URLSearchParams(params);
  const defaults = DEFAULT_DEALS_LIST_STATE;
  const set = (key: string, value: string, defaultValue: string) => {
    if (value === defaultValue) result.delete(key);
    else result.set(key, value);
  };
  set('tab', state.tab, defaults.tab);
  for (const tab of DEAL_TABS)
    set(`sort.${tab}`, state.sorts[tab], defaults.sorts[tab]);
  set('salesScope', state.sales.salesScope, defaults.sales.salesScope);
  result.delete('managerId');
  if (state.sales.salesScope === DealSalesScope.Selected) {
    for (const id of state.sales.managerIds) result.append('managerId', id);
  }
  set('q', state.searchText, '');
  const page = state.searchText.trim() === state.search ? state.page : 1;
  set('page', String(page), '1');
  return result;
}

const storageKey = (userId: string) => `deals-list-url:${userId}`;

export function rememberDealsListParams(
  userId: string | undefined,
  params: string
) {
  if (!userId) return;
  try {
    window.sessionStorage.setItem(storageKey(userId), params);
  } catch {
    // The list URL still works when browser storage is unavailable.
  }
}

export function getDealsListHref(userId: string | undefined) {
  if (!userId) return '/';
  try {
    const params = new URLSearchParams(
      window.sessionStorage.getItem(storageKey(userId)) ?? ''
    );
    const search = serializeDealsListParams(
      parseDealsListParams(params)
    ).toString();
    return search ? `/?${search}` : '/';
  } catch {
    return '/';
  }
}
